'use client';

import { Box } from '@mui/material';
import { useMemo, type PropsWithChildren } from 'react';

import { Loader } from '@/components/layout/loading';
import { DalContext } from '@/contexts/dalContext';
import { projectDalFactory } from '@/data/dal/dalFactory';

import { useEnvironment } from './useEnvironment';
import { useNonProjectDal } from './useNonProjectDal';
import { useUser } from './useUser';

export const DalProvider = ({ children }: PropsWithChildren) => {
  const { currentProjectId } = useUser();

  if (!currentProjectId) {
    return <LoaderComponent />;
  }

  return <ProviderWrapper>{children}</ProviderWrapper>;
};

const ProviderWrapper = ({ children }: PropsWithChildren) => {
  const { nonProjectDal, httpClient, configuration, lakehouseConfiguration } =
    useNonProjectDal();
  const { currentProjectId, isEmployee } = useUser();
  const { isTest } = useEnvironment();

  const dal = useMemo(() => {
    const projectDal = projectDalFactory(
      httpClient,
      configuration,
      lakehouseConfiguration,
      currentProjectId
    );

    const dal = { ...projectDal, ...nonProjectDal };

    if (isTest && typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, react-hooks/immutability
      (window as any).populateTestData = async () => {
        const urls = [
          `/projects/${currentProjectId}/populate?version=1`,
          `/projects/${currentProjectId}/metering/populate?version=1`,
          `/projects/${currentProjectId}/inventory/populate-backup-jobs?version=1`,
          `/projects/${currentProjectId}/visibility/populate?version=1`,
          `/projects/${currentProjectId}/inventory/populate?version=1`,
          `/projects/${currentProjectId}/search/populate?version=1`,
        ];

        for (const url of urls) {
          await httpClient.put(url, '{}');
        }
      };
    }

    if (isEmployee && typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, react-hooks/immutability
      (window as any).dal = dal;
    }

    return dal;
  }, [
    configuration,
    lakehouseConfiguration,
    currentProjectId,
    isTest,
    httpClient,
    isEmployee,
    nonProjectDal,
  ]);

  return <DalContext.Provider value={dal}>{children}</DalContext.Provider>;
};

const LoaderComponent = () => (
  <Box
    className='h-full'
    sx={{
      background: 'var(--mui-palette-background-pageGradient)',
    }}
  >
    <Loader />
  </Box>
);
